import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
    Video as VideoIcon,
    RotateCcw,
    Check,
    X,
    AlertCircle,
    Loader2,
    MapPinOff,
    Circle,
    Square,
} from 'lucide-react';
import { useGeolocation, useCamera } from '@/lib/contexts/device-permissions-context';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { useTranslation } from 'react-i18next';

const MAX_VIDEO_DURATION_SECONDS = 60;

function pickVideoMimeType() {
    if (typeof MediaRecorder === 'undefined') return null;
    const candidates = [
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/mp4',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
    ];
    return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) ?? '';
}

function extensionForMime(mime) {
    if (!mime) return 'webm';
    if (mime.includes('mp4')) return 'mp4';
    if (mime.includes('webm')) return 'webm';
    return 'webm';
}

/**
 * Sheet pour capturer une vidéo horodatée via la caméra (champ de procédure vidéo).
 *
 * Payload renvoyé à `onCapture` :
 *   { mediaType: 'video', mimeType, blob, fileName, dataUrl, durationMs }
 */
export default function VideoCaptureSheet({ open, onClose, onCapture, isUploading = false }) {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const previewVideoRef = useRef(null);
    const streamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const capturedBlobRef = useRef(null);

    const {
        isEnabled: isGeolocationEnabled,
        toggleGeolocation,
        permissionStatus: geoPermissionStatus,
    } = useGeolocation();

    const { getStream: getCameraStream, isSupported: isCameraSupported } = useCamera();

    const [cameraStatus, setCameraStatus] = useState('pending');
    const [errorMessage, setErrorMessage] = useState('');
    const [capturedVideoUrl, setCapturedVideoUrl] = useState(null);
    const [capturedVideoMime, setCapturedVideoMime] = useState(null);
    const [capturedVideoDuration, setCapturedVideoDuration] = useState(0);
    const [facingMode, setFacingMode] = useState('environment');
    const [isStartingCamera, setIsStartingCamera] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingElapsed, setRecordingElapsed] = useState(0);

    const isGeolocationActive = isGeolocationEnabled && geoPermissionStatus === 'granted';
    const hasCaptured = !!capturedVideoUrl;

    const videoMimeType = useMemo(() => pickVideoMimeType(), []);
    const isVideoSupported = typeof MediaRecorder !== 'undefined' && videoMimeType !== null;

    const startCamera = useCallback(async () => {
        if (!isGeolocationActive) return;

        if (!isCameraSupported) {
            setCameraStatus('error');
            setErrorMessage("La caméra n'est pas disponible sur cet appareil.");
            return;
        }

        setIsStartingCamera(true);
        setErrorMessage('');

        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }

            const constraints = {
                video: {
                    facingMode,
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: true,
            };

            const stream = await getCameraStream(constraints);

            if (!stream) {
                setCameraStatus('denied');
                setErrorMessage(
                    "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.",
                );
                return;
            }

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }

            setCameraStatus('granted');
        } catch (err) {
            console.error('[VideoCaptureSheet] Erreur accès caméra:', err);

            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setCameraStatus('denied');
                setErrorMessage(
                    "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.",
                );
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                setCameraStatus('error');
                setErrorMessage('Aucune caméra détectée sur cet appareil.');
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                setCameraStatus('error');
                setErrorMessage('La caméra est utilisée par une autre application.');
            } else {
                setCameraStatus('error');
                setErrorMessage("Impossible d'accéder à la caméra. Vérifiez vos paramètres.");
            }
        } finally {
            setIsStartingCamera(false);
        }
    }, [facingMode, isGeolocationActive, isCameraSupported, getCameraStream]);

    const clearRecordingTimer = useCallback(() => {
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
    }, []);

    const stopRecorder = useCallback(() => {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state !== 'inactive') {
            try {
                rec.stop();
            } catch (e) {
                console.warn('[VideoCaptureSheet] stop recorder failed', e);
            }
        }
        clearRecordingTimer();
    }, [clearRecordingTimer]);

    const stopCamera = useCallback(() => {
        stopRecorder();
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, [stopRecorder]);

    const startRecording = useCallback(() => {
        if (!streamRef.current || isRecording) return;
        if (!isVideoSupported) {
            setErrorMessage("L'enregistrement vidéo n'est pas supporté par ce navigateur.");
            setCameraStatus('error');
            return;
        }

        try {
            recordedChunksRef.current = [];
            const options = videoMimeType ? { mimeType: videoMimeType } : undefined;
            const recorder = new MediaRecorder(streamRef.current, options);
            mediaRecorderRef.current = recorder;

            const startedAt = Date.now();

            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(recordedChunksRef.current, {
                    type: recorder.mimeType || videoMimeType || 'video/webm',
                });
                const url = URL.createObjectURL(blob);
                capturedBlobRef.current = blob;
                setCapturedVideoUrl(url);
                setCapturedVideoMime(blob.type);
                setCapturedVideoDuration(Date.now() - startedAt);
                setIsRecording(false);
                clearRecordingTimer();

                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                    streamRef.current = null;
                }
                if (videoRef.current) {
                    videoRef.current.srcObject = null;
                }
            };

            recorder.start(250);
            setIsRecording(true);
            setRecordingElapsed(0);

            recordingTimerRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startedAt) / 1000);
                setRecordingElapsed(elapsed);
                if (elapsed >= MAX_VIDEO_DURATION_SECONDS) {
                    stopRecorder();
                }
            }, 250);
        } catch (err) {
            console.error('[VideoCaptureSheet] Erreur démarrage enregistrement:', err);
            setErrorMessage("Impossible de démarrer l'enregistrement vidéo.");
            setCameraStatus('error');
            setIsRecording(false);
        }
    }, [isRecording, videoMimeType, isVideoSupported, clearRecordingTimer, stopRecorder]);

    const stopRecording = useCallback(() => {
        stopRecorder();
    }, [stopRecorder]);

    const resetCapture = useCallback(() => {
        if (capturedVideoUrl) URL.revokeObjectURL(capturedVideoUrl);
        capturedBlobRef.current = null;
        setCapturedVideoUrl(null);
        setCapturedVideoMime(null);
        setCapturedVideoDuration(0);
        setRecordingElapsed(0);
    }, [capturedVideoUrl]);

    const retake = useCallback(() => {
        resetCapture();
        startCamera();
    }, [resetCapture, startCamera]);

    const toggleCamera = useCallback(() => {
        setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
    }, []);

    const confirmCapture = useCallback(() => {
        const blob = capturedBlobRef.current;
        if (!blob) return;
        const ext = extensionForMime(capturedVideoMime);
        const fileName = `video_${Date.now()}.${ext}`;
        onCapture({
            mediaType: 'video',
            mimeType: capturedVideoMime || `video/${ext}`,
            blob,
            fileName,
            dataUrl: capturedVideoUrl,
            durationMs: capturedVideoDuration,
        });
    }, [capturedVideoUrl, capturedVideoMime, capturedVideoDuration, onCapture]);

    const handleClose = useCallback(() => {
        if (isUploading || isRecording) return;
        stopCamera();
        resetCapture();
        setCameraStatus('pending');
        setErrorMessage('');
        onClose();
    }, [stopCamera, resetCapture, onClose, isUploading, isRecording]);

    useEffect(() => {
        if (open && !hasCaptured && isGeolocationActive) {
            const timer = setTimeout(() => startCamera(), 100);
            return () => clearTimeout(timer);
        }

        if (!open) {
            stopCamera();
            resetCapture();
            setCameraStatus('pending');
            setErrorMessage('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, hasCaptured, isGeolocationActive]);

    useEffect(() => {
        if (open && cameraStatus === 'granted' && !hasCaptured && !isRecording) {
            startCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facingMode]);

    useEffect(() => {
        return () => {
            if (capturedVideoUrl) URL.revokeObjectURL(capturedVideoUrl);
            clearRecordingTimer();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formattedElapsed = useMemo(() => {
        const m = Math.floor(recordingElapsed / 60)
            .toString()
            .padStart(2, '0');
        const s = (recordingElapsed % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    }, [recordingElapsed]);

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && !isUploading && !isRecording && handleClose()}>
            <SheetContent className="w-full! max-w-full! md:w-[600px]! md:max-w-[600px]! flex flex-col p-0 h-full">
                <SheetHeader className="px-6 py-4 border-b border-border shrink-0">
                    <SheetTitle className="text-lg font-semibold text-foreground">
                        {t('procedures:enregistrerUneVideoHorodatee')}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 flex flex-col items-center justify-center bg-foreground-dark relative overflow-hidden">
                    {isRecording && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-destructive/90 text-background px-3 py-1.5 rounded-full text-sm font-medium">
                            <span className="w-2 h-2 rounded-full bg-background animate-pulse" />
                            REC {formattedElapsed} / {MAX_VIDEO_DURATION_SECONDS.toString().padStart(2, '0')}s
                        </div>
                    )}

                    {!isGeolocationActive && (
                        <div className="flex flex-col items-center justify-center gap-4 text-background p-6 text-center">
                            <div className="w-16 h-16 bg-warning/20 rounded-full flex items-center justify-center">
                                <MapPinOff className="w-10 h-10 text-warning" />
                            </div>
                            <p className="text-base font-medium">{t('procedures:geolocalisationRequise')}</p>
                            <p className="text-sm text-background/70 max-w-xs">
                                {t('procedures:pourEnregistrerUneVideoHorodateeVousDevezActiver')}
                            </p>
                            <Button
                                variant="soft"
                                color="secondary"
                                onClick={toggleGeolocation}
                                className="mt-2 text-primary-foreground"
                            >
                                <MapPinOff className="w-4 h-4" />
                                {t('procedures:activerLaGeolocalisation')}
                            </Button>
                        </div>
                    )}

                    {isGeolocationActive && (cameraStatus === 'pending' || isStartingCamera) && !hasCaptured && (
                        <div className="flex flex-col items-center justify-center gap-4 text-background">
                            <Loader2 className="w-12 h-12 animate-spin" />
                            <p className="text-sm">{t('procedures:accesALaCamera')}</p>
                        </div>
                    )}

                    {isGeolocationActive &&
                        (cameraStatus === 'denied' || cameraStatus === 'error') &&
                        !isStartingCamera &&
                        !hasCaptured && (
                            <div className="flex flex-col items-center justify-center gap-4 text-background p-6 text-center">
                                <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center">
                                    <AlertCircle className="w-10 h-10 text-destructive" />
                                </div>
                                <p className="text-sm max-w-xs">{errorMessage}</p>
                                <Button variant="soft" color="secondary" onClick={startCamera} className="mt-2">
                                    <RotateCcw className="w-4 h-4" />
                                    {t('procedures:reessayer')}
                                </Button>
                            </div>
                        )}

                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`w-full h-full object-contain ${
                            isGeolocationActive && cameraStatus === 'granted' && !hasCaptured && !isStartingCamera
                                ? ''
                                : 'hidden'
                        }`}
                    />

                    {isGeolocationActive &&
                        cameraStatus === 'granted' &&
                        !hasCaptured &&
                        !isStartingCamera &&
                        !isRecording && (
                            <Button
                                variant="plain"
                                color="neutral"
                                size="auto"
                                onClick={toggleCamera}
                                className="absolute top-4 right-4 w-10 h-10 bg-foreground-dark/50 rounded-full flex items-center justify-center text-background hover:bg-foreground-dark/70 transition-colors"
                                title={t('procedures:changerDeCamera')}
                            >
                                <RotateCcw className="w-5 h-5" />
                            </Button>
                        )}

                    {isGeolocationActive && capturedVideoUrl && (
                        <video
                            ref={previewVideoRef}
                            src={capturedVideoUrl}
                            controls
                            playsInline
                            className="w-full h-full object-contain bg-foreground-dark"
                        />
                    )}
                </div>

                <SheetFooter className="px-6 py-4 border-t border-border shrink-0">
                    {!isGeolocationActive ? (
                        <div className="flex items-center justify-center w-full">
                            <Button
                                type="button"
                                variant="outline"
                                color="neutral"
                                onClick={handleClose}
                                disabled={isUploading}
                            >
                                <X className="w-4 h-4" />
                                {t('procedures:fermer')}
                            </Button>
                        </div>
                    ) : !hasCaptured ? (
                        <div className="flex items-center justify-center w-full gap-4">
                            <Button
                                type="button"
                                variant="soft"
                                color="neutral"
                                onClick={handleClose}
                                disabled={isUploading || isRecording}
                            >
                                <X className="w-4 h-4" />
                                {t('procedures:annuler')}
                            </Button>

                            {!isRecording ? (
                                <SubmitButton
                                    type="button"
                                    onClick={startRecording}
                                    disabled={
                                        cameraStatus !== 'granted' || isStartingCamera || !isVideoSupported
                                    }
                                >
                                    <Circle className="w-5 h-5 fill-current text-destructive" />
                                    {t('procedures:demarrerLEnregistrement')}
                                </SubmitButton>
                            ) : (
                                <SubmitButton type="button" onClick={stopRecording}>
                                    <Square className="w-5 h-5 fill-current" />
                                    {t('procedures:arreter')}
                                </SubmitButton>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center w-full gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                color="neutral"
                                onClick={retake}
                                disabled={isUploading}
                            >
                                <RotateCcw className="w-4 h-4" />
                                {t('procedures:reprendre')}
                            </Button>
                            <SubmitButton
                                type="button"
                                onClick={confirmCapture}
                                isSubmitting={isUploading}
                                loadingText={t('procedures:envoi2')}
                            >
                                <Check className="w-5 h-5" />
                                {t('procedures:valider')}
                            </SubmitButton>
                        </div>
                    )}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
